import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpSubmitEntityRequest, useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { SaleDiscount } from "./SaleDiscountData";
import { useAgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import { DiscountDataGetAllDiscountsQuery } from "./SaleDiscountDataQueries";
import { AgTechIndex, Index } from "@agtech/core/utilities/AgTechUtilities";
import { useAgTechPopUpContext } from "@agtech/core/functions/AgTechAppPopUps";

/// DTOs

export type SaleDiscountWriteDTO = {
    discount: SaleDiscount
}

export const useSaleDiscountDataWriteDiscountAction = (): AgTechDataAction<SaleDiscountWriteDTO, SaleDiscount, AgTechIndex<SaleDiscount>> => {
    const discountsQuery = useAgTechQuery({
        query: DiscountDataGetAllDiscountsQuery
    });

    const saleDiscountSubmitRequest = useAgTechHttpSubmitEntityRequest<SaleDiscount>();

    return {
        name: 'WriteDiscount',
        query: discountsQuery,
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity ? 'Updating discount...' : 'Creating discount...',
            actionConfirmationMessage: `Successfully ${props.originalEntity ? 'updated' : 'created' } discount`
        }),
        validate: async props  => {
            if (props.submittedEntity.discount.amount <= 0) {
                props.validation.failWithWarning("Please specify a valid discount amount");
            }

            if (props.submittedEntity.discount.isPercentage && props.submittedEntity.discount.amount > 100) {
                props.validation.failWithWarning(props.submittedEntity.discount.amount + '% is not a valid percentage');
            }

            const existingDiscounts = Object.values(props.context);

            const existingDiscountWithSameName = existingDiscounts.filter(disc =>
                disc.name === props.submittedEntity.discount.name &&
                disc.id !== props.submittedEntity.discount.id);

            if (existingDiscountWithSameName && existingDiscountWithSameName.length > 0) {
                props.validation.failWithWarning(`A discount with name "${props.submittedEntity.discount.name}" already exists. Please choose a different name.`);
            }
        },
        action: async props => {
            return await saleDiscountSubmitRequest.post({
                path: 'Discounts',
                postedData: props.submittedEntity.discount,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: DiscountDataGetAllDiscountsQuery,
                update: discounts => {
                    return {
                        ...discounts,
                        [props.responseData.id]: props.responseData
                    };
                }
            });
        }
    }
}

export const useSaleDiscountDataDeleteDiscountAction = (): AgTechDataAction<SaleDiscount, boolean> => {
    const popupContext = useAgTechPopUpContext();
    const discountDeletionRequest = useAgTechHttpPostRequest<SaleDiscount, boolean>();

    return {
        name: 'DeleteDiscount',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting discount...',
            actionConfirmationMessage: `Successfully deleted discount`
        }),
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: `Are you sure you want to delete discount "${props.submittedEntity.name}" from the system?`,
                    details: "This discount will no longer be available when creating invoices, but existing invoices with this discount will not be changed",
                    width: '500px',
                    confirmationButtonText: 'Delete Discount'
                },
                submission: {
                    actionExecutionMessage: 'Deleting discount...',
                    actionConfirmationMessage: 'Discount deleted'
                }
            })
        },
        action: async props => {
            return await discountDeletionRequest.post({
                path: 'Discounts/Delete',
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: DiscountDataGetAllDiscountsQuery,
                update: discounts => {
                    const updatedDiscounts = Object.values(discounts)
                        .filter(e => e.id !== props.submittedEntity.id);

                    return Index(updatedDiscounts, e => e.id);
                }
            });

            popupContext.closePopUp();
        }
    }
}